import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated } from "../../selectors";
import { useAuthentication } from "../../Authentication";
import {
  fetchUserOnboardingStatus,
  updateUserOnboardingData,
  formatOnboardingData,
} from "../utils/onboardingApi";
import {
  ONBOARDING_STEPS,
  getStepsForPathAndUserType,
  getInitialSteps,
  resolveStepOptions,
  isStepApplicable,
} from "../config/onboardingConfig";
import OnboardingStep from "../components/OnboardingStep";
import ProgressIndicator from "../components/ProgressIndicator";
import ThreeDotSpinner from "../../components/ThreeDotSpinner";

export default function UserOnboarding() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthentication();
  const authenticated = useSelector(selectAuthenticated);
  const accessToken = useSelector(selectAccessToken);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [path, setPath] = useState(null);
  const [userType, setUserType] = useState(null);
  const [steps, setSteps] = useState(() => getInitialSteps());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Hydrate auth state if not yet determined
  useEffect(() => {
    if (authenticated === null) {
      isAuthenticated();
    }
  }, [authenticated]);

  // Check onboarding status once auth state is known
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (authenticated === null) {
        return;
      }

      if (authenticated === false) {
        navigate("/signin", { state: { path: "/onboarding" } });
        return;
      }

      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        const result = await fetchUserOnboardingStatus(accessToken);
        
        if (result.isCompleted) {
          console.log("User has already completed onboarding, redirecting to playground")
          navigate("/", { state: { onboardingAlreadyChecked: true }, replace: true });
        } else {
          console.log("User needs to complete onboarding")
          setLoading(false);
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [authenticated, accessToken, navigate]);

  // Update steps when path or user type changes
  useEffect(() => {
    const newSteps = getStepsForPathAndUserType(path, userType);
    setSteps(newSteps);
  }, [path, userType]);

  // Save progress to server. Returns true if saved successfully, false otherwise (and sets error state).
  const saveProgressToServer = async (data) => {
    if (!accessToken || Object.keys(data).length === 0) return true;
    try {
      const formatted = formatOnboardingData(data);
      const result = await updateUserOnboardingData(accessToken, formatted);
      if (result.status === "error") {
        setError(result.message || "Could not save progress.");
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error saving onboarding progress:", err);
      setError("Could not save progress. Check your connection and try again.");
      return false;
    }
  };

  // Handle step completion — only advance after save succeeds (so user isn't left with unsaved progress on next step)
  const handleStepComplete = async (stepId, value) => {
    const updatedFormData = { ...formData, [stepId]: value };
    setFormData(updatedFormData);
    setError(null);
    setSubmitting(true);

    const saved = await saveProgressToServer(updatedFormData);
    setSubmitting(false);
    if (!saved) return;

    // Path step: set path and advance (never submit)
    if (stepId === ONBOARDING_STEPS.PATH.id) {
      setPath(value);
      setCurrentStepIndex(currentStepIndex + 1);
      return;
    }

    // User type step: set userType and advance to next *applicable* step (skip e.g. monthly_volume for non-brands)
    if (stepId === ONBOARDING_STEPS.USER_TYPE.id) {
      setUserType(value);
      const nextSteps = getStepsForPathAndUserType(path, value);
      let nextIndex = currentStepIndex + 1;
      while (nextIndex < nextSteps.length && !isStepApplicable(nextSteps[nextIndex], path, value)) {
        nextIndex += 1;
      }
      setCurrentStepIndex(Math.min(nextIndex, nextSteps.length - 1));
      return;
    }

    // Move to next step or submit
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      handleSubmit(updatedFormData);
    }
  };

  // Handle previous step (skip inapplicable steps, e.g. company_name for individuals)
  const handlePrevious = () => {
    let prevIndex = currentStepIndex - 1;
    while (prevIndex >= 0 && !isStepApplicable(steps[prevIndex], path, userType)) {
      prevIndex -= 1;
    }
    if (prevIndex >= 0) setCurrentStepIndex(prevIndex);
  };

  // Handle form submission
  const handleSubmit = async (finalFormData = formData) => {
    setSubmitting(true);
    setError(null);

    try {
      const formattedData = formatOnboardingData(finalFormData);
      const result = await updateUserOnboardingData(accessToken, formattedData);

      if (result.status === "success") {
        // Onboarding completed successfully, redirect to playground
        navigate("/");
      } else {
        setError(result.message || "Failed to save onboarding data");
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error submitting onboarding:", error);
      setError("An error occurred while saving your information");
      setSubmitting(false);
    }
  };

  // Handle skip — save "skipped" (and any partial progress) to backend so we don't show onboarding again; localStorage as fallback
  const handleSkip = async () => {
    const basePayload = Object.keys(formData).length > 0 ? formatOnboardingData(formData) : [];
    const payload = [...basePayload, { onboarding_step: "skipped", onboarding_value: "true" }];

    if (accessToken) {
      setSubmitting(true);
      try {
        const result = await updateUserOnboardingData(accessToken, payload);
        if (result.status === "success") {
          localStorage.setItem("OnboardingSkipped", "true");
          navigate("/");
          return;
        }
      } catch (err) {
        console.error("Error saving skip:", err);
      }
      setSubmitting(false);
    }
    // Fallback: no token or save failed — still let them leave; localStorage will prevent re-show on this device
    localStorage.setItem("OnboardingSkipped", "true");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-ground">
        <div className="text-center">
          <ThreeDotSpinner />
          <p className="mt-4 font-Mulish text-sm text-graphite">Loading…</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return null; // Will redirect to signin, or still hydrating (null)
  }

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const progress = totalSteps > 0 ? (currentStepIndex / totalSteps) * 100 : 0;

  return (
    <div className="h-screen w-full min-h-0 overflow-y-auto overflow-x-hidden bg-ground">
      <div className="mx-auto max-w-2xl min-h-full px-4 pb-24 pt-6 sm:px-6 sm:pt-8 md:px-8">
        <header className="mb-6 text-center sm:mb-8">
          <h1 className="font-newsreader text-4xl font-medium tracking-tight text-secondary">
            Welcome to TryOn AI
          </h1>
          <p className="mt-2 font-Mulish text-sm text-graphite">
            A few questions so the playground can start in the right place.
          </p>
        </header>

        <ProgressIndicator
          currentStep={currentStepIndex + 1}
          totalSteps={totalSteps}
          progress={progress}
          steps={steps}
        />

        {currentStep && (
          <div className="mb-6 rounded-2xl bg-white p-5 shadow-[0_0_0_1px_var(--hairline)] sm:p-6 md:p-8">
            <OnboardingStep
              step={resolveStepOptions(currentStep, formData)}
              value={formData[currentStep.id]}
              onComplete={handleStepComplete}
              onPrevious={currentStepIndex > 0 ? handlePrevious : null}
              isLastStep={
                currentStepIndex === steps.length - 1 &&
                currentStep.id !== ONBOARDING_STEPS.PATH.id &&
                currentStep.id !== ONBOARDING_STEPS.USER_TYPE.id
              }
              submitting={submitting}
            />
          </div>
        )}

        {error && (
          <div
            className="mb-6 rounded-2xl border-l-2 border-primary-500 bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] sm:p-5"
            role="alert"
          >
            <p className="font-Mulish text-sm font-semibold text-secondary">{error}</p>
            <p className="mt-1 font-Mulish text-sm text-graphite">
              Check your connection and try again, or skip for now.
            </p>
          </div>
        )}

        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleSkip}
            className="min-h-[44px] rounded-lg px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

